import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Card, Spinner, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import distributorService from '../../../../services/DistributorService';

const CollectFOSCollection = () => {
  const navigate = useNavigate();
  const [fosList, setFosList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [selectedFOS, setSelectedFOS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [collectionData, setCollectionData] = useState({
    amount: '',
    paymentMethod: 'Cash',
    remark: '',
    utr: ''
  });

  // Refs to prevent duplicate calls
  const hasInitialized = useRef(false);
  const searchTimeoutRef = useRef(null);

  // Fetch FOS profiles from API
  const fetchFOSProfiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: searchQuery
      };
      
      const response = await distributorService.getFOSProfiles(params);
      
      if (response.success) {
        // Map API data to component structure
        const mappedData = response.data.results.map(fos => ({
          id: fos.id,
          fostId: fos.user_id || 'N/A',
          name: fos.user_name || 'N/A',
          mobile: fos.user_mobile || fos.mobile_number,
          customerId: fos.user_customer_id || fos.customer_id,
          operator: fos.operator,
          creditLimit: parseFloat(fos.credit_limit) || 0,
          outstandingAmount: parseFloat(fos.outstanding_amount) || 0,
          distributorName: fos.distributor_name || 'N/A',
          distributorMobile: fos.distributor_mobile || 'N/A',
          // Calculate pending commission based on outstanding (example: 2% of outstanding)
          pendingCommission: (parseFloat(fos.outstanding_amount) || 0) * 0.02,
          commissionRate: 2.0, // Default commission rate
          // Mock data for collection history (this would come from another API endpoint)
          collectionHistory: [],
          lastCollection: new Date().toISOString()
        }));
        
        setFosList(mappedData);
        setTotalCount(response.data.count);
      } else {
        setError(response.error || 'Failed to fetch FOS profiles');
        toast.error(response.error || 'Failed to fetch FOS profiles');
      }
    } catch (err) {
      console.error('Error fetching FOS profiles:', err);
      setError('Failed to fetch FOS profiles');
      toast.error('Failed to fetch FOS profiles');
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch function
  const debouncedFetchProfiles = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      fetchFOSProfiles();
    }, 300);
  }, [currentPage, rowsPerPage, searchQuery]);

  // Initialize data on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchFOSSummary();
      fetchFOSProfiles();
    }
  }, []);

  // Handle search, pagination, and rowsPerPage changes
  useEffect(() => {
    if (hasInitialized.current) {
      if (searchQuery) {
        debouncedFetchProfiles();
      } else {
        fetchFOSProfiles();
      }
    }
  }, [currentPage, rowsPerPage, searchQuery, debouncedFetchProfiles]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Since we're using server-side filtering, we don't need client-side filtering
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  // Fetch FOS outstanding summary
  const fetchFOSSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await distributorService.getFOSOutstandingSummary();
      
      if (response.success) {
        setSummaryData(response.data);
      }
    } catch (error) {
      console.error('Error fetching FOS summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };



  const exportToExcel = () => {
    const exportData = fosList.map(fos => ({
      'FOS ID': fos.customerId,
      'FOS Name': fos.name,
      'Mobile': fos.mobile,
      'Outstanding Amount': fos.outstandingAmount,
      'Distributor': fos.distributorName,
      'Distributor Mobile': fos.distributorMobile
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'FOS_Profiles');
    XLSX.writeFile(workbook, `fos_profiles_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleCollect = (fos) => {
    setSelectedFOS(fos);
    setCollectionData({
      amount: '',
      paymentMethod: 'Cash',
      remark: '',
      utr: ''
    });
    setShowCollectionModal(true);
  };

  const handleCollectionSubmit = async (e) => {
    e.preventDefault();
    
    if (!collectionData.amount || isNaN(collectionData.amount)) {
      toast.error('Please enter a valid amount');
      return;
    }

    const collectionAmount = parseFloat(collectionData.amount);
    
    if (collectionAmount <= 0) {
      toast.error('Collection amount must be greater than zero');
      return;
    }
    
    if (collectionAmount > selectedFOS.outstandingAmount) {
      toast.error('Collection amount cannot exceed outstanding amount');
      return;
    }

    setLoading(true);
    try {
      // Map payment method to payment mode
      const getPaymentMode = (method) => {
        switch (method.toLowerCase()) {
          case 'cash': return 'CASH';
          case 'upi': return 'UPI';
          
        }
      };

      const transactionData = {
        fosProfileId: selectedFOS.id,
        amount: collectionAmount,
        transactionType: 'DEBIT',
        txnType: 'COLLECT',
        paymentMode: getPaymentMode(collectionData.paymentMethod),
        remark: collectionData.remark || '',
        utr: collectionData.utr || ''
      };

      const response = await distributorService.recordFOSTransaction(transactionData);
      
      if (response.success) {
        const { transaction, balance_summary } = response.data;
        
        // Update local state with new outstanding amount from API response
        setFosList(prev => prev.map(f => 
          f.id === selectedFOS.id ? { 
            ...f, 
            outstandingAmount: balance_summary.current_outstanding,
            lastCollection: transaction.transaction_date,
            collectionHistory: [
              { 
                date: transaction.transaction_date, 
                amount: transaction.amount, 
                type: 'Collection',
                method: transaction.payment_mode,
                reference: transaction.order_id,
                collectedBy: transaction.collected_by_mobile,
                transactionId: transaction.id
              },
              ...f.collectionHistory
            ]
          } : f
        ));
        
        toast.success(response.message || `₹${collectionAmount.toLocaleString()} collected from ${selectedFOS.name}`);
        setShowCollectionModal(false);
        setSelectedFOS(null);
        
        // Refresh the data to get updated outstanding amounts
        fetchFOSProfiles();
        fetchFOSSummary();
      } else {
        toast.error(response.error || 'Failed to record collection');
      }
    } catch (error) {
      toast.error('Failed to record collection');
      console.error('Error recording collection:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCollectionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getCreditUtilizationBadge = (outstanding, creditLimit) => {
    if (creditLimit === 0) return <Badge bg="secondary">No Limit</Badge>;
    const percentage = (outstanding / creditLimit) * 100;
    if (percentage > 100) return <Badge bg="danger">{percentage.toFixed(0)}% - Over Limit</Badge>;
    if (percentage >= 90) return <Badge bg="warning">{percentage.toFixed(0)}%</Badge>;
    if (percentage >= 70) return <Badge bg="info">{percentage.toFixed(0)}%</Badge>;
    return <Badge bg="success">{percentage.toFixed(0)}%</Badge>;
  };

  const getCommissionColor = (amount) => {
    if (amount > 8000) return 'text-success fw-bold';
    if (amount > 5000) return 'text-warning fw-bold';
    return 'text-info fw-bold';
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-users-cog me-2"></i>
          FOS Collections
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
        </div>
      </div>


      {/* Filters */}
      <Row className="mb-4">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search FOS..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={4}>
          <div className="d-flex gap-2">
            {summaryLoading ? (
              <div className="bg-light rounded p-2 text-center flex-fill">
                <Spinner animation="border" size="sm" />
                <small className="ms-2">Loading...</small>
              </div>
            ) : summaryData ? (
              <>
                <div className="bg-light rounded p-2 text-center flex-fill">
                  <small className="text-muted d-block">Total Outstanding : <span className="text-danger fs-5">₹{Math.floor(summaryData.total_outstanding || 0).toLocaleString()}</span></small>

                </div>
                <div className="bg-light rounded p-2 text-center flex-fill">
                  <small className="text-muted d-block">Total FOS : <span className="text-primary fs-5">{summaryData.total_fos || 0}</span></small>

                </div>
              </>
            ) : (
              <div className="bg-light rounded p-2 text-center flex-fill">
                <small className="text-muted">No data</small>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Table */}
      <div className="table-responsive">
        <Table striped bordered hover className="modern-table">
          <thead>
            <tr>
              <th>Actions</th>
              <th>FOS Info</th>
              <th>Outstanding</th>
              <th>Distributor</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  <Spinner animation="border" role="status" className="me-2" />
                  Loading FOS profiles...
                </td>
              </tr>
            ) : fosList.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center py-4">
                  No FOS profiles found
                </td>
              </tr>
            ) : (
              fosList.map((fos) => (
              <tr key={fos.id}>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleCollect(fos)}
                      title="Collect Payment"
                    >
                      <i className="fas fa-money-bill-wave"></i>
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => navigate(`/distributor/collect/fos-collection/${fos.id}/${fos.fostId}`)}
                      title="View Collection History"
                    >
                      <i className="fas fa-history"></i>
                    </Button>
                  </div>
                </td>
                <td>
                  <div>
                    <div><strong>{fos.name}</strong></div>
                    <small className="text-muted">{fos.mobile}</small>
                    <br />
                    <small className="text-muted">ID: {fos.customerId}</small>
                  </div>
                </td>
                <td>
                  <div className={fos.outstandingAmount > 0 ? 'text-danger fw-bold' : 'text-success'}>
                    ₹{fos.outstandingAmount.toLocaleString()}
                  </div>
                </td>
                <td>
                  <div>
                    <div>{fos.distributorName}</div>
                    <small className="text-muted">{fos.distributorMobile}</small>
                  </div>
                </td>
              </tr>
            ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {fosList.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, totalPages - 4 + index));
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Collection Modal */}
      <Modal show={showCollectionModal} onHide={() => setShowCollectionModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Collect from FOS</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCollectionSubmit}>
          <Modal.Body>
            {selectedFOS && (
              <div>
                <div className="mb-3">
                  <strong>FOS:</strong> {selectedFOS.name}
                </div>
                <div className="mb-3">
                  <strong>Mobile:</strong> {selectedFOS.mobile}
                </div>
                <div className="mb-4">
                  <strong>Outstanding Amount:</strong> 
                  <span className="text-danger fw-bold ms-2">
                    ₹{selectedFOS.outstandingAmount.toLocaleString()}
                  </span>
                </div>
                
                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Collection Amount *</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>₹</InputGroup.Text>
                        <Form.Control
                          type="number"
                          value={collectionData.amount}
                          onChange={(e) => handleInputChange('amount', e.target.value)}
                          placeholder="Enter amount"
                          min="0"
                          max={selectedFOS.outstandingAmount}
                          required
                        />
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Maximum: ₹{selectedFOS.outstandingAmount.toLocaleString()}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Payment Method *</Form.Label>
                      <Form.Select
                        value={collectionData.paymentMethod}
                        onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                        required
                      >
                        <option value="Cash">Cash</option>
                        <option value="UPI">UPI</option>
                        
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  {collectionData.paymentMethod === 'UPI' && (
                    <Col md={6} className="mb-3">
                      <Form.Group>
                        <Form.Label>UTR Number *</Form.Label>
                        <Form.Control
                          type="text"
                          value={collectionData.utr}
                          onChange={(e) => handleInputChange('utr', e.target.value)}
                          placeholder="Enter UTR number"
                          required
                        />
                      </Form.Group>
                    </Col>
                  )}
                  
                  <Col md={collectionData.paymentMethod === 'UPI' ? 6 : 12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Remark</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={collectionData.remark}
                        onChange={(e) => handleInputChange('remark', e.target.value)}
                        placeholder="Enter any remarks or notes"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCollectionModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button variant="success" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-check me-2"></i>
                  Record Collection
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default CollectFOSCollection;